import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, signal } from "@angular/core";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { Location } from "../../../pojo/Location";
import { Proto } from "../../../pojo/Proto";
import { S25ItemI } from "../../../pojo/S25ItemI";
import { S25Util } from "../../../util/s25-util";
import { SpaceService } from "../../../services/space.service";

@TypeManagerDecorator("s25-ng-location-details-hours")
@Component({
    selector: "s25-ng-location-details-hours",
    template: `
        @if (init()) {
            @for (hour of formattedHours; track hour.itemId) {
                <div class="c-margin-bottom--half">
                    <label class="ngBold">{{ hour.itemName }}</label>
                    @if (canEdit) {
                        <s25-toggle-button
                            [falseLabel]="'Always Closed'"
                            [trueLabel]="'Always Open'"
                            [(value)]="hour.isActive"
                            (valueChange)="save(hour, $event, $index)"
                        ></s25-toggle-button>
                        <div class="time-wrapper">
                            <s25-timepicker [(modelValue)]="hour.open" (modelValueChange)="save(hour)"></s25-timepicker
                            >-
                            <s25-timepicker
                                [(modelValue)]="hour.close"
                                (modelValueChange)="save(hour)"
                            ></s25-timepicker>
                        </div>
                    } @else {
                        <p>{{ hour.itemDesc }}</p>
                    }
                </div>
            }
        }
    `,
    styles: `
        :host {
            display: block;
            padding: 1rem;

            .time-wrapper {
                display: flex;
                align-items: center;
                gap: 5px;

                s25-timepicker {
                    width: 130px;
                }
            }
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class S25LocationDetailsHoursComponent implements OnInit {
    @Input({ required: true }) itemId: number;
    @Input({ required: true }) hours: Location.HourDayId[] = [];
    @Input({ required: true }) is24Hour: boolean;
    @Input({ required: true }) canEdit: boolean;

    init = signal(false);
    formattedHours: FormattedHour[] = [];

    constructor(private cd: ChangeDetectorRef) {}

    ngOnInit() {
        const startOfDay = "00:00";
        const endOfDay = "23:59";
        this.hours ??= [];
        this.formattedHours =
            this.hours?.map((hour) => {
                const open = hour.open ?? startOfDay,
                    close = hour.close ?? endOfDay,
                    openLabel = this.is24Hour ? open : this.convertTo12Hour(open),
                    closeLabel = this.is24Hour ? close : this.convertTo12Hour(close),
                    alwaysOpen = open === startOfDay && close === endOfDay,
                    alwaysClosed = open === startOfDay && close === startOfDay;

                return {
                    itemId: hour.dayId,
                    itemName: hour.dayName,
                    itemDesc:
                        open === close
                            ? "Closed All Day"
                            : alwaysOpen
                              ? "Open All Day"
                              : `${openLabel} to ${closeLabel}`,
                    open: open,
                    close: close,
                    isActive: alwaysOpen ? true : alwaysClosed ? false : null,
                };
            }) ?? [];
        this.init.set(true);
    }

    convertTo12Hour(time: string) {
        const [hours, minutes] = time.split(":");
        const hour = +hours % 12 || 12;
        return `${hour}:${minutes} ${+hours < 12 ? "AM" : "PM"}`;
    }

    async save(dayData: FormattedHour, alwaysOpen?: boolean, index?: number) {
        let { open, close, itemName } = dayData;
        const dayPrefToggled = S25Util.isDefined(alwaysOpen);
        if (dayPrefToggled) {
            open = "00:00:00";
            close = alwaysOpen ? "23:59:59" : "00:00:00";
        } else {
            dayData.isActive = null;
        }
        const payload = {
            hours: [
                {
                    open: S25Util.date.toS25ISOTimeStr(open),
                    close: S25Util.date.toS25ISOTimeStr(close),
                    dayOfWeek: <Proto.DowNameLowercase>itemName.toLowerCase(),
                },
            ],
        };
        const [_, error] = await S25Util.Maybe(SpaceService.microUpdate({ id: this.itemId, ...payload }));
        if (error) return console.error(error);
        if (dayPrefToggled) this.formattedHours[index] = { ...dayData, open, close };
        this.cd.markForCheck();
    }
}

type FormattedHour = S25ItemI & { isActive: boolean } & Pick<Location.Hour, "open" | "close">;
